/**
 * 最小 ZIP リーダー (import で .zip を受け取るため)。
 *
 * - STORE (method 0) は格納バイトをそのまま取り出す。
 * - DEFLATE (method 8) はブラウザ標準の `DecompressionStream('deflate-raw')` で展開する。
 * - 外部依存は持たない (zip.ts と同方針 — Docker named-volume の node_modules 同期回避)。
 * - 暗号化 ZIP / ZIP64 / その他の圧縮方式は非対応 (該当時は UnzipError)。
 *
 * サイズは Central Directory の値を正とする (data descriptor 使用 ZIP でも安全)。
 */

const SIG_EOCD = 0x06_05_4b_50
const SIG_CDH = 0x02_01_4b_50

export class UnzipError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'UnzipError'
  }
}

/** raw DEFLATE バイト列をブラウザ標準 API で展開する。 */
const inflateRaw = async (data: Uint8Array): Promise<Uint8Array> => {
  if (typeof DecompressionStream === 'undefined') {
    throw new UnzipError('deflate_unsupported')
  }

  const stream = new Blob([data as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'))

  const buf = await new Response(stream).arrayBuffer()

  return new Uint8Array(buf)
}

/**
 * ZIP バイト列を「ファイル名 → 展開後バイト列」の map に変換する。
 * ディレクトリエントリ (末尾 `/`) は除外する。
 */
export const unzip = async (bytes: Uint8Array): Promise<Record<string, Uint8Array>> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  // End Of Central Directory を末尾から探索する (ZIP comment 長は最大 65535)。
  let eocd = -1
  const minPos = Math.max(0, bytes.length - 22 - 0xff_ff)
  for (let i = bytes.length - 22; i >= minPos; i--) {
    if (view.getUint32(i, true) === SIG_EOCD) {
      eocd = i
      break
    }
  }
  if (eocd === -1) throw new UnzipError('not_a_zip')

  const entryCount = view.getUint16(eocd + 10, true)
  const cdOffset = view.getUint32(eocd + 16, true)

  const decoder = new TextDecoder()
  const out: Record<string, Uint8Array> = {}
  let p = cdOffset

  for (let n = 0; n < entryCount; n++) {
    if (p + 46 > bytes.length || view.getUint32(p, true) !== SIG_CDH) {
      throw new UnzipError('corrupt')
    }
    const method = view.getUint16(p + 10, true)
    const compSize = view.getUint32(p + 20, true)
    const nameLen = view.getUint16(p + 28, true)
    const extraLen = view.getUint16(p + 30, true)
    const commentLen = view.getUint16(p + 32, true)
    const lhOffset = view.getUint32(p + 42, true)
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen))

    // Local File Header からデータ開始位置を求める (name/extra 長は LFH 側を使う)。
    const lhNameLen = view.getUint16(lhOffset + 26, true)
    const lhExtraLen = view.getUint16(lhOffset + 28, true)
    const dataStart = lhOffset + 30 + lhNameLen + lhExtraLen
    const raw = bytes.subarray(dataStart, dataStart + compSize)

    if (!name.endsWith('/')) {
      if (method === 0) {
        out[name] = raw
      } else if (method === 8) {
        out[name] = await inflateRaw(raw)
      } else {
        throw new UnzipError('unsupported_method')
      }
    }

    p += 46 + nameLen + extraLen + commentLen
  }

  return out
}
